import {
  Box,
  Grid,
  TextField,
  Typography,
  Paper,
  IconButton,
  Button,
  Avatar,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect, useCallback } from "react";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import toast, { Toaster } from "react-hot-toast";
import { useParams, useLocation } from "react-router-dom";
import * as services from "../../services/saleng";
import NoPhotographyOutlinedIcon from "@mui/icons-material/NoPhotographyOutlined";
import { formatNumber } from "./../../utils/formatNumber";
import { formatDate } from "../../utils/formatDate";

const GroupPricingPage = () => {
  const { id } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});
  const [users, setUsers] = useState([]);

  const [userSearch, setUserSearch] = useState("");
  const [searchText, setSearchText] = useState("");

  const { search } = useLocation();
  const q = new URLSearchParams(search);
  const name = q.get("name") ?? "";
  const detail = q.get("detail") ?? "";

  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredProducts = products.filter(
    (p) =>
      p.title.toLowerCase().includes(searchText.toLowerCase()) ||
      p.category.includes(searchText) ||
      p.subCategory.includes(searchText)
  );
  console.log(filteredProducts);

  const fetchData = useCallback(async () => {
    const res = await services.getProducts();
    const products = res.data.data;

    const adjRes = await services.getProductGroupsAdjustments(id);
    const adjustments = adjRes.data.data;

    const userRes = await services.getCustomers(id);
    const users = userRes.data.data;

    const productsWithIndex = products.map((item, index) => {
      const adjItem = adjustments.find((a) => a.productId === item.id);
      const adjustment = adjItem?.adjustment ?? 0;
      return {
        ...item,
        no: index + 1,
        category: item.category.name,
        subCategory: item.subCategory.name,
        adjustment,
        total: (item.price ?? 0) + adjustment,
        adjItem: !!adjItem,
        code: item.code,
      };
    });

    setProducts(productsWithIndex);
    setUsers(users);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processRowUpdate = async (newRow) => {
    newRow.total = Number(newRow.price) + Number(newRow.adjustment);

    const payload = {
      productId: newRow.id,
      adjustment: newRow.adjustment,
    };

    try {
      if (newRow.adjItem) {
        // มี adjustment อยู่แล้ว → ทำการ update
        await services.updateProductGroupsAdjustments(payload, id);
        console.log("🔄 update");
      } else {
        // ยังไม่มี adjustment → สร้างใหม่
        await services.createProductGroupsAdjustments(payload, id);
        console.log("✨ create");

        newRow.adjItem = true;
      }

      setProducts((prev) =>
        prev.map((p) => (p.id === newRow.id ? { ...p, ...newRow } : p))
      );
      toast.success("อัปเดตราคาสำเร็จแล้ว");
      return { ...newRow, isNew: false };
    } catch (error) {
      console.error("Error updating row:", error);
      toast.error("เกิดข้อผิดพลาดในการอัปเดตราคา");
      throw error;
    }
  };

  return (
    <Box>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          จัดการราคา
        </Typography>
        {/* <Button color="success" variant="contained" onClick={() => { setEditData(null); setOpenDialog(true); }}>เพิ่มกลุ่มลูกค้า</Button> */}
      </Box>
      <Grid container spacing={2}>
        {/* LEFT COLUMN - Users */}
        <Grid item xs={12} md={3}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="ชื่อกลุ่มลูกค้า"
              fullWidth
              value={name}
              disabled
              sx={{
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f0f0f0",
                  color: "#000",
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#888",
                },
              }}
            />
            <TextField
              label="รายละเอียด"
              fullWidth
              value={detail}
              multiline
              rows={4}
              disabled
              sx={{
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f0f0f0",
                  color: "#000",
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#888",
                },
              }}
            />
          </Box>
          <TextField
            fullWidth
            placeholder="พิมพ์เพื่อค้นหาจากลิสต์ด้านล่าง"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
            sx={{ mt: 2 }}
          />
          <Paper
            variant="outlined"
            sx={{ mt: 2, maxHeight: 600, overflow: "auto" }}
          >
            {filteredUsers.map((user) => (
              <Box
                key={user.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid #eee",
                }}
              >
                <Avatar src={user.img} sx={{ width: 50, height: 50, mr: 1 }}>
                  {!user.img && <NoPhotographyOutlinedIcon fontSize="large" />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography>{user.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {user.personType === "corporate"
                      ? "นิติบุคคล"
                      : "บุคคลธรรมดา"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN - Products */}
        <Grid item xs={12} md={9}>
          <TextField
            fullWidth
            label="ค้นหา"
            placeholder="ชื่อสินค้า / หมวดหมู่"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ maxWidth: { xs: "100%", sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 2 }}>
            <DataGrid
              rows={filteredProducts
                .slice() // สร้าง copy ของ array ป้องกัน mutate
                .sort((a, b) => a.code.localeCompare(b.code)) // เรียง code จากน้อยไปมาก
                .map((p, idx) => ({
                  id: p.id,
                  no: idx + 1,
                  ...p,
                }))}
              columns={[
                { field: "code", headerName: "รหัสสินค้า", flex: 0, width: 70 },
                // {
                //   field: "image",
                //   headerName: "รูปภาพ",
                //   flex: 0,
                //   width: 100,
                //   renderCell: (params) => {
                //     if (params.value) {
                //       return (
                //         <img
                //           src={params.value}
                //           style={{ width: "50px", height: "50px" }}
                //           alt=""
                //         />
                //       );
                //     }
                //   },
                // },
                { field: "title", headerName: "ชื่อสินค้า", flex: 2 },
                { field: "category", headerName: "หมวดหมู่", flex: 1 },
                { field: "subCategory", headerName: "หมวดหมู่ย่อย", flex: 1 },
                { field: "description", headerName: "คำอธิบาย", flex: 1.5 },
                {
                  field: "price",
                  headerName: "ราคาซื้อทั่วไป",
                  type: "number",
                  flex: 1.5,
                  minWidth: 120,
                  align: "right",
                  headerAlign: "right",
                  renderCell: (params) => {
                    const value = params.value ?? 0;
                    return (
                      <Box
                        display={"flex"}
                        justifyContent={"flex-end"}
                        alignItems={"center"}
                        width={"100%"}
                        gap={4}
                      >
                        {formatNumber(value)}
                      </Box>
                    );
                  },
                },
                {
                  field: "adjustment",
                  headerName: "ราคาเพิ่ม/ลด ต่อหน่วย",
                  type: "number",
                  editable: true,
                  flex: 1.5,
                  align: "right",
                  headerAlign: "right",
                  renderCell: (params) => {
                    const value = params.value ?? 0;
                    const color = value < 0 ? "#f44336" : "#4caf50";
                    return (
                      <Box
                        display={"flex"}
                        justifyContent={"flex-end"}
                        alignItems={"center"}
                        width={"100%"}
                        gap={4}
                        color={color}
                      >
                        {formatNumber(value)}
                      </Box>
                    );
                  },
                },
                {
                  field: "total",
                  headerName: "ราคารวม",
                  type: "number",
                  flex: 1.5,
                  align: "right",
                  headerAlign: "right",
                  renderCell: (params) => {
                    const value = params.value;
                    return (
                      <Box
                        display={"flex"}
                        justifyContent={"flex-end"}
                        alignItems={"center"}
                        width={"100%"}
                        gap={4}
                      >
                        {formatNumber(value)}
                      </Box>
                    );
                  },
                },
                {
                  field: "updatedAt",
                  headerName: "แก้ไขล่าสุด",
                  flex: 2,
                  renderCell: (params) => formatDate(params.value),
                },

                // {
                //   field: "description",
                //   headerName: "ข้อมูลเพิ่มเติม",
                //   flex: 0,
                //   sortable: false,
                //   filterable: false,
                //   disableColumnMenu: true,
                //   renderCell: (params) => {
                //     console.log("params", params);
                //     return (
                //       <SubjectIcon
                //         onClick={() => window.open("/reports/" + params.row.transactionId)}
                //       />
                //     );
                //   },
                // },
              ]}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={setRowModesModel}
              processRowUpdate={processRowUpdate}
              loading={loading}
              // pageSizeOptions={[25, 50, 100]}
              // initialState={{
              //   pagination: { paginationModel: { page: 0, pageSize: 25 } },
              // }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GroupPricingPage;
